import React, {useEffect, useState, forwardRef, useImperativeHandle} from 'react';
import {Col, Form, message, Row, Badge} from "antd";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {useIntl} from "react-intl";
import {NewTripRequestPageMessages} from "../NewTripRequestPageMessages";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {defineMessages} from "react-intl";
import {useAirportFilter} from "../../../../../utils/hooks/useAirportFilter";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {DateAndTimePicker} from "../../../../../components/UI/AntCustom/DateAndTimePicker";
import {isDateLessTanOneMinuteAgo} from "../../../../../utils/helpers/dateHelpers/scheduleDateHelpers";
import {fetchPlaneCategories} from "../../../../../utils/helpers/selectDataFetchers/fetchPlaneCategories";
import {Dayjs} from "dayjs";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import styled from "styled-components";
import {CompanyMultiSelector, CompanySelectorOption} from "./CompanyMultiSelector";
import {
  brokerAircraftSearch,
  clearBrokerAircraftSearch,
} from "../../../../../store/reducers/newRequestReducer/newRequestReducer";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {AdvancedSearchModal} from "./AdvancedSearchModal";
import {getDateAndTimePickerValidationRules} from "../../../../../utils/helpers/formHelpers/getDateAndTimePickerValidationRules";

const FormContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FieldsRow = styled(Row)`
  width: 100%;
`;

const ButtonsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: ${props => props.isMobile ? '10px' : '24px'};
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  flex-direction: ${props => props.isMobile ? 'column' : 'row'};
  
  button {
    ${props => props.isMobile ? 'width: 100%;' : ''}
  }
  
  .ant-badge {
    ${props => props.isMobile ? 'width: 100%;' : ''}
    
    .ant-badge-count {
      min-width: 20px !important;
      height: 20px !important;
      border-radius: 10px !important;
      padding: 0 6px !important;
      line-height: 20px !important;
      font-size: 12px !important;
      box-sizing: border-box !important;
      ${props => props.isMobile ? 'right: 7px !important;' : ''}
    }
  }
`;

const OneWayFormMessages = defineMessages({
  showAdvancedSearch: {
    id: "newTripRequestPage.showAdvancedSearch",
    defaultMessage: "Показать расширенный поиск",
  },
});

interface IOneWayFormData {
  airportFromId: number;
  airportToId: number;
  departureDate: Dayjs;
  pax: number;
  planeCategoryIds: number[],
  companionsAllowed: boolean;
  comment: string;
  companyIds: number[];
}

interface IOneWayFormProps {
  clearTrigger?: number;
}

export const OneWayForm = forwardRef<{ resetForm: () => void }, IOneWayFormProps>(({ clearTrigger }, ref) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [companyList, setCompanyList] = useState<CompanySelectorOption[]>([]);
  const [isAdvancedSearchModalOpen, setIsAdvancedSearchModalOpen] = useState<boolean>(false);
  
  // Хуки для фильтрации аэропортов
  const fromAirportFilter = useAirportFilter(form, 'airportToId');
  const toAirportFilter = useAirportFilter(form, 'airportFromId');
  const {
    isLoading: isSearchLoading,
  } = useAppSelector(state => state.newRequest.brokerAircraftSearch);

  useEffect(() => {
    return () => {
      dispatch(clearBrokerAircraftSearch())
    }
  }, [])

  // Сброс формы при изменении clearTrigger
  useEffect(() => {
    if (clearTrigger && clearTrigger > 0) {
      form.resetFields();
      setCompanyList([]);
    }
  }, [clearTrigger, form]);

  // Экспортируем метод resetForm через ref
  useImperativeHandle(ref, () => ({
    resetForm: () => {
      form.resetFields();
      setCompanyList([]);
    }
  }), []);

  const onFinish = (values: IOneWayFormData) => {
    // Извлекаем дату и время из Dayjs объекта
    // Всегда передаем время, так как оно нужно для определения местоположения самолета по Schedule
    const departureDate = values.departureDate.format('YYYY-MM-DD');
    const departureTime = values.departureDate.isSame(values.departureDate.startOf('day'))
      ? '00:00:00'
      : values.departureDate.format('HH:mm');

    // Получаем данные из расширенного поиска
    const advancedSearchValues = form.getFieldsValue(['planeModelIds', 'productionYearRange', 'overhaulYearRange', 
      'flightDurationRange', 'landingsCount', 'refuelsCount', 'planeUpdateDate', 'amenitiesIds', 'operatorIds', 'safetyStandardsIds']);

    // Преобразуем данные из расширенного поиска
    const planeModel = advancedSearchValues.planeModelIds?.length > 0 
      ? advancedSearchValues.planeModelIds.join(', ') 
      : undefined;
    
    const manufactureYearFrom = advancedSearchValues.productionYearRange?.[0];
    const manufactureYearTo = advancedSearchValues.productionYearRange?.[1];
    
    const renovationYearFrom = advancedSearchValues.overhaulYearRange?.[0];
    const renovationYearTo = advancedSearchValues.overhaulYearRange?.[1];
    
    const positioningDurationFromHours = advancedSearchValues.flightDurationRange?.[0];
    const positioningDurationToHours = advancedSearchValues.flightDurationRange?.[1];
    
    // Обрабатываем landingCount и refuelingCount
    let landingCountFrom: number | undefined;
    let landingCountTo: number | undefined;
    if (advancedSearchValues.landingsCount && Array.isArray(advancedSearchValues.landingsCount)) {
      const landingValues = advancedSearchValues.landingsCount.map((v: string) => {
        if (v === 'no-landings') return 0;
        if (v === 'one-landing') return 1;
        if (v === 'two-plus-landings') return 2;
        return undefined;
      }).filter((v: number | undefined) => v !== undefined) as number[];
      if (landingValues.length > 0) {
        landingCountFrom = Math.min(...landingValues);
        landingCountTo = Math.max(...landingValues) === 2 ? undefined : Math.max(...landingValues);
      }
    }
    
    let refuelingCountFrom: number | undefined;
    let refuelingCountTo: number | undefined;
    if (advancedSearchValues.refuelsCount && Array.isArray(advancedSearchValues.refuelsCount)) {
      const refuelValues = advancedSearchValues.refuelsCount.map((v: string) => {
        if (v === 'no-refuels') return 0;
        if (v === 'one-refuel') return 1;
        if (v === 'two-refuels') return 2;
        if (v === 'three-plus-refuels') return 3;
        return undefined;
      }).filter((v: number | undefined) => v !== undefined) as number[];
      if (refuelValues.length > 0) {
        refuelingCountFrom = Math.min(...refuelValues);
        refuelingCountTo = Math.max(...refuelValues) === 3 ? undefined : Math.max(...refuelValues);
      }
    }
    
    // Преобразуем updatedAtFrom из даты
    const updatedAtFrom = advancedSearchValues.planeUpdateDate 
      ? advancedSearchValues.planeUpdateDate.format('YYYY-MM-DD')
      : undefined;
    
    // Преобразуем amenities из расширенного поиска
    const amenities: string[] = [];
    if (advancedSearchValues.amenitiesIds && Array.isArray(advancedSearchValues.amenitiesIds)) {
      amenities.push(...advancedSearchValues.amenitiesIds.filter((id: string) => 
        ['air_ambulance', 'animals_aboard', 'smoking_aboard', 'air_cargo_freight', 'wifi', 'oven_aboard'].includes(id)
      ));
    }

    // Преобразуем operatorIds из модалки расширенного поиска
    const operatorIds = advancedSearchValues.operatorIds && Array.isArray(advancedSearchValues.operatorIds) && advancedSearchValues.operatorIds.length > 0
      ? advancedSearchValues.operatorIds
      : undefined;

    dispatch(brokerAircraftSearch({
      originAirportId: values.airportFromId,
      destinationAirportId: values.airportToId,
      departureDate,
      departureTime,
      pax: values.pax,
      planeCategoryIds: values.planeCategoryIds || [],
      planeModel,
      manufactureYearFrom,
      manufactureYearTo,
      renovationYearFrom,
      renovationYearTo,
      positioningDurationFromHours,
      positioningDurationToHours,
      landingCountFrom,
      landingCountTo,
      refuelingCountFrom,
      refuelingCountTo,
      updatedAtFrom,
      operatorIds,
      amenities: amenities.length > 0 ? amenities : undefined,
      page: 0,
      limit: 10,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const initialValues: Partial<IOneWayFormData> = {
    airportFromId: undefined,
    airportToId: undefined,
    departureDate: undefined,
    pax: undefined,
    planeCategoryIds: [],
    companionsAllowed: false,
    comment: undefined,
    companyIds: [],
  }

  const companyMultiSelector = <CompanyMultiSelector selectedCompanies={companyList}
                                                     setSelectedCompanies={setCompanyList}/>
  
  // Подсчитываем количество примененных фильтров advanced search
  const advancedSearchFieldsCount = (() => {
    let count = 0;
    
    // Компании (отдельный компонент, не из модалки)
    if (companyList.length > 0) count++;
    
    // Получаем все значения из формы
    const formValues = form.getFieldsValue([
      'planeModelIds', 
      'productionYearRange', 
      'overhaulYearRange', 
      'flightDurationRange', 
      'landingsCount', 
      'refuelsCount', 
      'planeUpdateDate', 
      'amenitiesIds', 
      'operatorIds', 
      'safetyStandardsIds',
      'companionsAllowed',
      'comment'
    ]);
    
    // Модель самолета
    if (formValues.planeModelIds && Array.isArray(formValues.planeModelIds) && formValues.planeModelIds.length > 0) count++;
    
    // Ползунки - учитываем только если они установлены в форме (модалка была открыта и сохранена)
    if (formValues.productionYearRange && Array.isArray(formValues.productionYearRange) && formValues.productionYearRange.length === 2) count++;
    if (formValues.overhaulYearRange && Array.isArray(formValues.overhaulYearRange) && formValues.overhaulYearRange.length === 2) count++;
    if (formValues.flightDurationRange && Array.isArray(formValues.flightDurationRange) && formValues.flightDurationRange.length === 2) count++;
    
    // Количество посадок
    if (formValues.landingsCount && Array.isArray(formValues.landingsCount) && formValues.landingsCount.length > 0) count++;
    
    // Количество дозаправок
    if (formValues.refuelsCount && Array.isArray(formValues.refuelsCount) && formValues.refuelsCount.length > 0) count++;
    
    // Дата обновления самолета
    if (formValues.planeUpdateDate) count++;
    
    // Удобства на борту
    if (formValues.amenitiesIds && Array.isArray(formValues.amenitiesIds) && formValues.amenitiesIds.length > 0) count++;
    
    // Операторы
    if (formValues.operatorIds && Array.isArray(formValues.operatorIds) && formValues.operatorIds.length > 0) count++;
    
    // Стандарты безопасности
    if (formValues.safetyStandardsIds && Array.isArray(formValues.safetyStandardsIds) && formValues.safetyStandardsIds.length > 0) count++;
    
    // Разрешены спутники
    if (formValues.companionsAllowed) count++;
    
    // Комментарий
    if (formValues.comment && formValues.comment.trim().length > 0) count++;
    
    return count;
  })();
  
  const handleAdvancedSearchSave = (values: any) => {
    form.setFieldsValue({
      comment: values.comment,
      companionsAllowed: values.companionsAllowed,
      planeModelIds: values.planeModelIds,
      productionYearRange: values.productionYearRange,
      overhaulYearRange: values.overhaulYearRange,
      flightDurationRange: values.flightDurationRange,
      landingsCount: values.landingsCount,
      refuelsCount: values.refuelsCount,
      planeUpdateDate: values.planeUpdateDate,
      amenitiesIds: values.amenitiesIds,
      operatorIds: values.operatorIds,
      safetyStandardsIds: values.safetyStandardsIds,
    });
  };

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      autoComplete="off"
      layout={'vertical'}
      form={form}
    >
      <FormContentWrapper>
        <FieldsRow gutter={isMobile ? [8, 8] : [20, 0]}>
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.fromAirportLabel)}
                name={'airportFromId'}
                rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}>
                <DebounceSelect
                  key={fromAirportFilter.key}
                  fetchOptions={fromAirportFilter.fetchOptions}
                  placeholder={intl.formatMessage(CommonFormMessages.fromAirportPlaceholder)}
                  showSearch={true}
                />
              </CustomFieldInput>
            </Col>
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.toAirportLabel)}
                name={'airportToId'}
                rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}>
                <DebounceSelect
                  key={toAirportFilter.key}
                  fetchOptions={toAirportFilter.fetchOptions}
                  placeholder={intl.formatMessage(CommonFormMessages.toAirportPlaceholder)}
                  showSearch={true}
                />
              </CustomFieldInput>
            </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.dateLabel)} (UTC)` + '*'}
                name={'departureDate'}
                rules={getDateAndTimePickerValidationRules(
                  intl,
                  true
                )}>
                <DateAndTimePicker
                  format="YYYY-MM-DD"
                  placeholder={intl.formatMessage(CommonFormMessages.datePlaceholder)}
                  disabledDate={isDateLessTanOneMinuteAgo}
                inputReadOnly={true} 
                placement={"bottomLeft"}
                  getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                />
              </CustomFieldInput>
            </Col>
          <Col xs={24} sm={24} md={4} lg={4} xl={4}>
              <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.paxLabel) + '*'}
                name={'pax'}
                rules={[
                  {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                  {min: 1, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinPax)},
                  {max: 100, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMaxPax)}
                ]}>
              <CustomInputNumber 
                controls={true} 
                placeholder={intl.formatMessage(CommonFormMessages.paxPlaceholder)}
                style={{width: "100%", height: "48px"}}
              />
              </CustomFieldInput>
            </Col>
        </FieldsRow>

        <FieldsRow gutter={8}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.planeCategoryLabel)}
                name={'planeCategoryIds'}
              >
                <DebounceSelect
                  fetchOptions={fetchPlaneCategories}
                  placeholder={intl.formatMessage(CommonFormMessages.allPlaceholder)}
                  mode={'multiple'}
                  brightPlaceholder={true}
                />
              </CustomFieldInput>
            </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <ButtonsWrapper isMobile={isMobile} style={{marginTop: '28px', justifyContent: isMobile ? 'stretch' : 'flex-end'}}>
              <div style={{width: isMobile ? '100%' : 'auto'}}>
                <Badge count={advancedSearchFieldsCount} offset={[-4, 4]} style={{display: isMobile ? 'block' : 'inline-block'}}>
                  <CustomButton
                    onClick={() => setIsAdvancedSearchModalOpen(true)}
                    style={{
                      border: '1px solid #448ef8',
                      color: '#000',
                      background: '#fff',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    + {intl.formatMessage(OneWayFormMessages.showAdvancedSearch)}
                  </CustomButton>
                </Badge>
              </div>
          <CustomButton
            type="primary"
            htmlType="submit"
                loading={isSearchLoading}
                style={{minWidth: isMobile ? '100%' : '119px', width: isMobile ? '100%' : 'auto'}}
          >
                {intl.formatMessage(CommonButtonMessages.find)}
          </CustomButton>
            </ButtonsWrapper>
      </Col>
        </FieldsRow>

      </FormContentWrapper>

      <AdvancedSearchModal
        isModalVisible={isAdvancedSearchModalOpen}
        setIsModalVisible={setIsAdvancedSearchModalOpen}
        onSave={handleAdvancedSearchSave}
        initialValues={{
          comment: form.getFieldValue('comment'),
          companionsAllowed: form.getFieldValue('companionsAllowed'),
          planeModelIds: form.getFieldValue('planeModelIds'),
          productionYearRange: form.getFieldValue('productionYearRange'),
          overhaulYearRange: form.getFieldValue('overhaulYearRange'),
          flightDurationRange: form.getFieldValue('flightDurationRange'),
          landingsCount: form.getFieldValue('landingsCount'),
          refuelsCount: form.getFieldValue('refuelsCount'),
          planeUpdateDate: form.getFieldValue('planeUpdateDate'),
          amenitiesIds: form.getFieldValue('amenitiesIds'),
          operatorIds: form.getFieldValue('operatorIds'),
          safetyStandardsIds: form.getFieldValue('safetyStandardsIds'),
        }}
      />
    </Form>
  );
});
